"use client";

import { useCallback, useEffect, useState } from "react";
import {
  computeAiDailyBrief,
  computeCategoryGroups,
  computeDashboardStats,
  computeExploreSuggestions,
  computeInboxOverview,
  computeSubscriptionSummaries,
  improveBy,
  whatsGood,
  type ScanProgress,
} from "@/lib/emailCleaner/compute";
import { formatBytes, formatCount, timeAgo } from "@/lib/emailCleaner/format";
import { cleanupHistoryStore, dismissedSuggestionsStore, rulesStore, senderFlagsStore } from "@/lib/emailCleaner/stores";
import { useBulkActionWithProgress } from "@/lib/emailCleaner/useBulkActionWithProgress";
import { useViewStack } from "@/lib/emailCleaner/useViewStack";
import type { BulkActionType, DashboardStats, ExploreSuggestion, SenderGroup, SubscriptionSummary } from "@/lib/emailCleaner/types";
import { ActionsMenu, BulkActionProgressOverlay, Card, EmptyState, ScanProgressBar, ScreenHeader } from "../Shared";

export type HomeView =
  | { name: "explore" }
  | { name: "inbox-overview" }
  | { name: "unsubscribe" }
  | { name: "block-mute" }
  | { name: "stop-unwanted" }
  | { name: "auto-organize" }
  | { name: "daily-brief" }
  | { name: "recent-activity" }
  | { name: "category"; label: string; query: string; action: "archive" | "trash" };

type AccountProps = { accessToken: string; accountEmail: string };
type Props = AccountProps & { initialView: HomeView; onExit: () => void };

export default function HomeTab({ accessToken, accountEmail, initialView, onExit }: Props) {
  const { view, depth, push, pop } = useViewStack<HomeView>(initialView);
  const back = depth > 1 ? pop : onExit;

  switch (view.name) {
    case "explore":
      return <ExploreScreen accessToken={accessToken} accountEmail={accountEmail} onBack={back} onOpenCategory={(label, query, action) => push({ name: "category", label, query, action })} />;
    case "category":
      return <CategoryScreen accessToken={accessToken} accountEmail={accountEmail} label={view.label} query={view.query} action={view.action} onBack={back} />;
    case "inbox-overview":
      return <InboxOverviewScreen accessToken={accessToken} accountEmail={accountEmail} onBack={back} />;
    case "unsubscribe":
      return <UnsubscribeScreen accessToken={accessToken} accountEmail={accountEmail} onBack={back} />;
    case "block-mute":
      return <BlockMuteScreen onBack={back} />;
    case "stop-unwanted":
      return <StopUnwantedScreen accessToken={accessToken} accountEmail={accountEmail} onBack={back} />;
    case "auto-organize":
      return <AutoOrganizeScreen onBack={back} />;
    case "daily-brief":
      return <DailyBriefScreen accessToken={accessToken} accountEmail={accountEmail} onBack={back} />;
    case "recent-activity":
      return <RecentActivityScreen onBack={back} />;
  }
}

function useScanProgress() {
  const [progress, setProgress] = useState<ScanProgress | null>(null);
  return { progress, setProgress };
}

function SuggestionRow({
  s,
  onDismiss,
  onAction,
  onMoreAction,
  onView,
}: {
  s: ExploreSuggestion;
  onDismiss: () => void;
  onAction: () => void;
  onMoreAction: (action: BulkActionType, labelName?: string) => void;
  onView?: () => void;
}) {
  const isTrash = s.action === "trash";
  return (
    <Card className="mb-3">
      {onView ? (
        <button type="button" onClick={onView} className="flex w-full items-start justify-between gap-3 text-left">
          <SuggestionRowInfo s={s} isTrash={isTrash} />
        </button>
      ) : (
        <div className="flex items-start justify-between gap-3">
          <SuggestionRowInfo s={s} isTrash={isTrash} />
        </div>
      )}
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={onDismiss}
          className="flex-1 rounded-full border border-slate-300 py-1.5 text-xs font-semibold text-slate-600 dark:border-slate-700 dark:text-slate-300"
        >
          Dismiss
        </button>
        <button
          type="button"
          onClick={onAction}
          className={`flex-[2] rounded-full py-1.5 text-xs font-semibold text-white ${isTrash ? "bg-red-600" : "bg-violet-600"}`}
        >
          {isTrash ? "Trash" : "Archive"} ({s.count})
        </button>
        <ActionsMenu onChoose={onMoreAction} />
      </div>
    </Card>
  );
}

function SuggestionRowInfo({ s, isTrash }: { s: ExploreSuggestion; isTrash: boolean }) {
  return (
    <>
      <div className="min-w-0">
        <p className="font-medium text-slate-900 dark:text-white">{s.title}</p>
        {!s.isCategory && <p className="text-xs text-slate-500 dark:text-slate-400">{s.subtitle}</p>}
        <p className="text-xs text-slate-400 dark:text-slate-500">{isTrash ? "Messages in Inbox → Trash" : "Messages in Inbox → Archive"}</p>
      </div>
      <span className="shrink-0 text-sm font-semibold text-slate-700 dark:text-slate-300">{s.count}</span>
    </>
  );
}

function ExploreScreen({
  accessToken,
  accountEmail,
  onBack,
  onOpenCategory,
}: AccountProps & { onBack: () => void; onOpenCategory: (label: string, query: string, action: "archive" | "trash") => void }) {
  const [suggestions, setSuggestions] = useState<ExploreSuggestion[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { progress: scanProgress, setProgress: setScanProgress } = useScanProgress();
  const { run, progress: bulkProgress } = useBulkActionWithProgress(accessToken);

  const load = useCallback(() => {
    setSuggestions(null);
    setError(null);
    computeExploreSuggestions(accessToken, accountEmail, setScanProgress)
      .then(setSuggestions)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load suggestions"));
  }, [accessToken, accountEmail, setScanProgress]);

  useEffect(() => {
    // Loading real Gmail-backed data on mount/param change is the intended sync effect here.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    load();
  }, [load]);

  async function handleAction(s: ExploreSuggestion) {
    const count = await run({ query: s.query, actionType: s.action, historyLabel: s.title, estimatedCount: s.count });
    if (count === null) return;
    dismissedSuggestionsStore.dismiss(s.key);
    load();
  }

  async function handleMoreAction(s: ExploreSuggestion, action: BulkActionType, labelName?: string) {
    const count = await run({ query: s.query, actionType: action, historyLabel: s.title, estimatedCount: s.count, moveToLabelName: labelName });
    if (count === null) return;
    if (action === "archive" || action === "trash" || action === "keepNewest") dismissedSuggestionsStore.dismiss(s.key);
    load();
  }

  function handleDismiss(s: ExploreSuggestion) {
    dismissedSuggestionsStore.dismiss(s.key);
    load();
  }

  return (
    <div>
      <BulkActionProgressOverlay progress={bulkProgress} />
      <ScreenHeader title="Suggestions" onBack={onBack} />
      {error && <Card className="text-sm text-red-600 dark:text-red-400">{error}</Card>}
      {!error && !suggestions && scanProgress && <ScanProgressBar {...scanProgress} />}
      {suggestions && suggestions.length === 0 && <EmptyState>No cleanup suggestions right now 🎉</EmptyState>}
      {suggestions?.map((s) => (
        <SuggestionRow
          key={s.key}
          s={s}
          onDismiss={() => handleDismiss(s)}
          onAction={() => handleAction(s)}
          onMoreAction={(action, labelName) => handleMoreAction(s, action, labelName)}
          onView={s.isCategory ? () => onOpenCategory(s.title, s.query, s.action) : undefined}
        />
      ))}
    </div>
  );
}

function CategoryScreen({
  accessToken,
  accountEmail,
  label,
  query,
  action,
  onBack,
}: AccountProps & { label: string; query: string; action: "archive" | "trash"; onBack: () => void }) {
  const [groups, setGroups] = useState<SenderGroup[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { run, progress: bulkProgress } = useBulkActionWithProgress(accessToken);

  const load = useCallback(() => {
    setGroups(null);
    setError(null);
    computeCategoryGroups(accessToken, accountEmail, query)
      .then(setGroups)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load"));
  }, [accessToken, accountEmail, query]);

  useEffect(() => {
    // Loading real Gmail-backed data on mount/param change is the intended sync effect here.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    load();
  }, [load]);

  async function handleAction(g: SenderGroup, actionType: BulkActionType = action, labelName?: string) {
    const count = await run({ query: `${query} from:${g.senderEmail}`, actionType, historyLabel: g.senderName, estimatedCount: g.totalCount, moveToLabelName: labelName });
    if (count === null) return;
    if (actionType === "archive" || actionType === "trash" || actionType === "keepNewest") dismissedSuggestionsStore.dismiss(g.senderEmail);
    load();
  }

  const isTrash = action === "trash";

  return (
    <div>
      <BulkActionProgressOverlay progress={bulkProgress} />
      <ScreenHeader title={label} onBack={onBack} />
      {error && <Card className="text-sm text-red-600 dark:text-red-400">{error}</Card>}
      {!error && !groups && <ScanProgressBar stage="Loading senders…" current={0} total={1} />}
      {groups && groups.length === 0 && <EmptyState>No messages found in {label}</EmptyState>}
      {groups?.map((g) => (
        <Card key={g.senderEmail} className="mb-3">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="font-medium text-slate-900 dark:text-white">{g.senderName}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">{g.senderEmail}</p>
            </div>
            <div className="text-right">
              <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">{g.totalCount}</p>
              <p className="text-xs text-slate-400">{timeAgo(g.lastMessageAt)}</p>
            </div>
          </div>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={() => handleAction(g)}
              className={`flex-1 rounded-full py-1.5 text-xs font-semibold text-white ${isTrash ? "bg-red-600" : "bg-violet-600"}`}
            >
              {isTrash ? "Trash" : "Archive"}
            </button>
            <ActionsMenu onChoose={(actionType, labelName) => handleAction(g, actionType, labelName)} />
          </div>
        </Card>
      ))}
    </div>
  );
}

function InboxOverviewScreen({ accessToken, accountEmail, onBack }: AccountProps & { onBack: () => void }) {
  const [groups, setGroups] = useState<SenderGroup[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { progress, setProgress } = useScanProgress();
  const { run, progress: bulkProgress } = useBulkActionWithProgress(accessToken);

  const load = useCallback(() => {
    setGroups(null);
    setError(null);
    computeInboxOverview(accessToken, accountEmail, setProgress)
      .then(setGroups)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load"));
  }, [accessToken, accountEmail, setProgress]);

  useEffect(() => {
    // Loading real Gmail-backed data on mount/param change is the intended sync effect here.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    load();
  }, [load]);

  async function handleAction(g: SenderGroup, actionType: BulkActionType = "archive", labelName?: string) {
    const count = await run({ query: `from:${g.senderEmail}`, actionType, historyLabel: g.senderName, estimatedCount: g.totalCount, moveToLabelName: labelName });
    if (count === null) return;
    load();
  }

  return (
    <div>
      <BulkActionProgressOverlay progress={bulkProgress} />
      <ScreenHeader title="Inbox" onBack={onBack} />
      {error && <Card className="text-sm text-red-600 dark:text-red-400">{error}</Card>}
      {!error && !groups && progress && <ScanProgressBar {...progress} />}
      {groups && groups.length === 0 && <EmptyState>No recent mail found</EmptyState>}
      {groups?.map((g) => (
        <Card key={g.senderEmail} className="mb-3">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className={`truncate font-medium ${g.isRead ? "text-slate-700 dark:text-slate-300" : "font-bold text-slate-900 dark:text-white"}`}>{g.senderName}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">{g.category || g.senderEmail}</p>
            </div>
            <div className="text-right">
              <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">{g.totalCount}</p>
              <p className="text-xs text-slate-400">{timeAgo(g.lastMessageAt)}</p>
            </div>
          </div>
          <div className="mt-3 flex gap-2">
            <button type="button" onClick={() => handleAction(g)} className="flex-1 rounded-full bg-violet-600 py-1.5 text-xs font-semibold text-white">
              Archive
            </button>
            <ActionsMenu onChoose={(actionType, labelName) => handleAction(g, actionType, labelName)} />
          </div>
        </Card>
      ))}
    </div>
  );
}

function UnsubscribeScreen({ accessToken, accountEmail, onBack }: AccountProps & { onBack: () => void }) {
  const [subs, setSubs] = useState<SubscriptionSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const { progress, setProgress } = useScanProgress();
  const { run, progress: bulkProgress } = useBulkActionWithProgress(accessToken);

  const load = useCallback(() => {
    setSubs(null);
    setError(null);
    computeSubscriptionSummaries(accessToken, accountEmail, setProgress)
      .then(setSubs)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load subscriptions"));
  }, [accessToken, accountEmail, setProgress]);

  useEffect(() => {
    // Loading real Gmail-backed data on mount/param change is the intended sync effect here.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    load();
  }, [load]);

  // Removing just the affected row locally avoids re-running the expensive
  // full mailbox scan (200-message sample + per-sender counts + AI call)
  // after every single action, which was the "reloads" behavior reported.
  function removeRow(senderEmail: string) {
    setSubs((prev) => prev?.filter((s) => s.senderEmail !== senderEmail) ?? prev);
  }

  async function unsubscribeOne(s: SubscriptionSummary) {
    setBusy(s.senderEmail);
    let ok = false;
    if (s.unsubscribeUrl) {
      try {
        await fetch(s.unsubscribeUrl, { mode: "no-cors" });
        ok = true;
      } catch {
        ok = false;
      }
    }
    if (!ok) {
      senderFlagsStore.setFlag(s.senderEmail, { muted: true });
    }
    setBusy(null);
    removeRow(s.senderEmail);
  }

  async function handleMoreAction(s: SubscriptionSummary, action: BulkActionType, labelName?: string) {
    const count = await run({
      query: `from:${s.senderEmail}`,
      actionType: action,
      historyLabel: s.senderName,
      estimatedCount: s.emailCount,
      moveToLabelName: labelName,
    });
    if (count === null) return;
    removeRow(s.senderEmail);
  }

  return (
    <div>
      <BulkActionProgressOverlay progress={bulkProgress} />
      <ScreenHeader title="Unsubscribe from Mailing Lists" onBack={onBack} />
      {error && <Card className="text-sm text-red-600 dark:text-red-400">{error}</Card>}
      {!error && !subs && progress && <ScanProgressBar {...progress} />}
      {subs && subs.length === 0 && <EmptyState>No mailing lists detected</EmptyState>}
      {subs?.map((s) => (
        <Card key={s.senderEmail} className="mb-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate font-medium text-slate-900 dark:text-white">{s.senderName}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {s.emailCount} emails • {s.frequency}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={() => unsubscribeOne(s)}
              disabled={busy === s.senderEmail}
              className="rounded-full border border-violet-300 px-3 py-1.5 text-xs font-semibold text-violet-700 disabled:opacity-60 dark:border-violet-700 dark:text-violet-400"
            >
              {busy === s.senderEmail ? "…" : "Unsubscribe"}
            </button>
            <ActionsMenu onChoose={(action, labelName) => handleMoreAction(s, action, labelName)} />
          </div>
        </Card>
      ))}
    </div>
  );
}

function BlockMuteScreen({ onBack }: { onBack: () => void }) {
  const [email, setEmail] = useState("");
  const [flags, setFlags] = useState<Record<string, { muted?: boolean; blocked?: boolean }>>({});

  const load = useCallback(() => {
    setFlags(senderFlagsStore.load());
  }, []);

  useEffect(() => {
    // Loading real Gmail-backed data on mount/param change is the intended sync effect here.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    load();
  }, [load]);

  function block(addr: string) {
    if (!window.confirm(`This will delete all future emails matching "${addr}" (via an automation rule). Continue?`)) return;
    senderFlagsStore.setFlag(addr, { blocked: true, muted: true });
    rulesStore.save([
      ...rulesStore.load(),
      {
        id: crypto.randomUUID(),
        description: `Block ${addr} — delete future emails`,
        conditionType: "senderContains",
        conditionValue: addr,
        actionType: "deleteAfterDays",
        actionValue: "0",
        isActive: true,
      },
    ]);
    setEmail("");
    load();
  }

  function unblock(addr: string) {
    senderFlagsStore.setFlag(addr, { blocked: false, muted: false });
    load();
  }

  const entries = Object.entries(flags).filter(([, f]) => f.muted || f.blocked);

  return (
    <div>
      <ScreenHeader title="Block or Mute a Sender" onBack={onBack} />
      <div className="flex gap-2">
        <input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter an email address to block"
          className="flex-1 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 focus:border-violet-400 focus:outline-none dark:border-slate-700 dark:bg-[#0f172a] dark:text-slate-200"
        />
        <button
          type="button"
          onClick={() => email.trim() && block(email.trim())}
          className="rounded-full bg-red-600 px-4 py-2 text-sm font-semibold text-white"
        >
          Block
        </button>
      </div>

      {entries.length > 0 && (
        <div className="mt-5">
          <p className="mb-2 text-sm font-semibold text-slate-900 dark:text-white">Blocked &amp; Muted</p>
          {entries.map(([addr, f]) => (
            <Card key={addr} className="mb-2 flex items-center justify-between">
              <span className="truncate text-sm text-slate-700 dark:text-slate-300">{addr}</span>
              <button type="button" onClick={() => unblock(addr)} className="text-sm font-medium text-violet-600 dark:text-violet-400">
                {f.blocked ? "Unblock" : "Unmute"}
              </button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function StopUnwantedScreen({ accessToken, accountEmail, onBack }: AccountProps & { onBack: () => void }) {
  const [suggestions, setSuggestions] = useState<ExploreSuggestion[] | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const { run, progress: bulkProgress } = useBulkActionWithProgress(accessToken);

  useEffect(() => {
    computeExploreSuggestions(accessToken, accountEmail).then((all) => {
      const unwanted = all.filter((s) => s.action === "trash");
      setSuggestions(unwanted);
      setSelected(new Set(unwanted.map((s) => s.key)));
    });
  }, [accessToken, accountEmail]);

  async function stopAll() {
    if (!suggestions) return;
    const targets = suggestions.filter((s) => selected.has(s.key));
    const totalCount = targets.reduce((a, s) => a + s.count, 0);
    if (!window.confirm(`This will delete an estimated ${formatCount(totalCount)} emails across ${targets.length} sources and block matching senders. Continue?`)) return;

    setRunning(true);
    let totalCleaned = 0;
    let blocked = 0;
    for (const s of targets) {
      const count = (await run({ query: s.query, actionType: "trash", historyLabel: s.title, estimatedCount: s.count, skipConfirmation: true })) ?? 0;
      totalCleaned += count;
      if (!s.isCategory) {
        rulesStore.save([
          ...rulesStore.load(),
          {
            id: crypto.randomUUID(),
            description: `Block future emails from ${s.title}`,
            conditionType: "senderContains",
            conditionValue: s.key,
            actionType: "deleteAfterDays",
            actionValue: "0",
            isActive: true,
          },
        ]);
        blocked += 1;
      }
    }
    setRunning(false);
    setResult(`Cleaned ${totalCleaned} emails and blocked ${blocked} senders`);
  }

  const totalSelectedCount = suggestions?.filter((s) => selected.has(s.key)).reduce((a, s) => a + s.count, 0) ?? 0;

  return (
    <div>
      <BulkActionProgressOverlay progress={bulkProgress} />
      <ScreenHeader title="Stop All Unwanted Mail" onBack={onBack} />
      {!suggestions && <ScanProgressBar stage="Scanning inbox…" current={0} total={1} />}
      {suggestions && suggestions.length === 0 && <EmptyState>No unwanted mail sources detected right now 🎉</EmptyState>}
      {result && <Card className="mb-4 text-sm text-emerald-700 dark:text-emerald-400">{result}</Card>}
      {suggestions && suggestions.length > 0 && (
        <>
          <Card className="mb-4 text-sm text-slate-600 dark:text-slate-400">
            These senders/categories look unwanted. Selected items will have their existing mail trashed and future mail blocked automatically.
          </Card>
          {suggestions.map((s) => {
            const checked = selected.has(s.key);
            return (
              <Card key={s.key} className="mb-2">
                <label className="flex cursor-pointer items-center gap-3">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() =>
                      setSelected((prev) => {
                        const next = new Set(prev);
                        if (checked) next.delete(s.key);
                        else next.add(s.key);
                        return next;
                      })
                    }
                    className="h-4 w-4 accent-red-600"
                  />
                  <div className="min-w-0">
                    <p className="truncate font-medium text-slate-900 dark:text-white">{s.title}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{s.count} emails</p>
                  </div>
                </label>
              </Card>
            );
          })}
          <button
            type="button"
            onClick={stopAll}
            disabled={running || selected.size === 0}
            className="mt-4 w-full rounded-full bg-red-600 py-3 text-sm font-semibold text-white disabled:opacity-60"
          >
            {running ? "Working…" : `Stop ${selected.size} sources (${formatCount(totalSelectedCount)} emails)`}
          </button>
        </>
      )}
    </div>
  );
}

const AUTO_ORGANIZE_TEMPLATES = [
  { title: "Auto-archive read newsletters", description: "Newsletters are archived automatically once you've read them.", conditionType: "isNewsletter" as const, actionType: "archiveOnRead" as const, actionValue: "" },
  { title: "Auto-delete old verification codes", description: "OTP and verification-code emails are deleted after 7 days.", conditionType: "isOtp" as const, actionType: "deleteAfterDays" as const, actionValue: "7" },
  { title: "Auto-archive read promotions", description: "Promotional emails are archived automatically once you've read them.", conditionType: "isPromotion" as const, actionType: "archiveOnRead" as const, actionValue: "" },
];

function AutoOrganizeScreen({ onBack }: { onBack: () => void }) {
  const [rules, setRules] = useState(rulesStore.load());

  function findExisting(t: (typeof AUTO_ORGANIZE_TEMPLATES)[number]) {
    return rules.find((r) => r.conditionType === t.conditionType && r.actionType === t.actionType);
  }

  function toggle(t: (typeof AUTO_ORGANIZE_TEMPLATES)[number], enable: boolean) {
    const current = rulesStore.load();
    if (enable) {
      rulesStore.save([
        ...current,
        { id: crypto.randomUUID(), description: t.title, conditionType: t.conditionType, conditionValue: "", actionType: t.actionType, actionValue: t.actionValue, isActive: true },
      ]);
    } else {
      const existing = findExisting(t);
      if (existing) rulesStore.save(current.filter((r) => r.id !== existing.id));
    }
    setRules(rulesStore.load());
  }

  return (
    <div>
      <ScreenHeader title="Clean &amp; Organize Automatically" onBack={onBack} />
      {AUTO_ORGANIZE_TEMPLATES.map((t) => {
        const existing = findExisting(t);
        return (
          <Card key={t.title} className="mb-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="font-medium text-slate-900 dark:text-white">{t.title}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">{t.description}</p>
            </div>
            <input
              type="checkbox"
              checked={!!existing}
              onChange={(e) => toggle(t, e.target.checked)}
              className="h-5 w-5 shrink-0 accent-violet-600"
            />
          </Card>
        );
      })}
    </div>
  );
}

function DailyBriefScreen({ accessToken, accountEmail, onBack }: AccountProps & { onBack: () => void }) {
  const [brief, setBrief] = useState<string | null>(null);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    computeAiDailyBrief(accessToken, accountEmail)
      .then(setBrief)
      .catch((err) => setError(err instanceof Error ? err.message : "Could not reach the AI gateway."));
    computeDashboardStats(accessToken, accountEmail).then(setStats).catch(() => {});
  }, [accessToken, accountEmail]);

  return (
    <div>
      <ScreenHeader title="AI Daily Brief" onBack={onBack} />
      <Card>
        <p className="mb-2 font-semibold text-slate-900 dark:text-white">Today&apos;s Brief</p>
        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        {!error && !brief && <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>}
        {brief && <p className="text-sm text-slate-700 dark:text-slate-300">{brief}</p>}
      </Card>
      {stats && (
        <Card className="mt-4">
          <p className="mb-2 font-semibold text-slate-900 dark:text-white">Top Priorities</p>
          {whatsGood(stats).map((t, i) => (
            <p key={i} className="text-sm text-emerald-700 dark:text-emerald-400">• {t}</p>
          ))}
          <p className="mt-3 mb-2 font-semibold text-slate-900 dark:text-white">Improve By</p>
          {improveBy(stats).map((t, i) => (
            <p key={i} className="text-sm text-amber-700 dark:text-amber-400">• {t}</p>
          ))}
        </Card>
      )}
    </div>
  );
}

function RecentActivityScreen({ onBack }: { onBack: () => void }) {
  const [entries, setEntries] = useState<ReturnType<typeof cleanupHistoryStore.load>>([]);

  useEffect(() => {
    // Hydrating from localStorage (an external store) must happen after mount.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setEntries(cleanupHistoryStore.load());
  }, []);

  return (
    <div>
      <ScreenHeader title="Recent Activity" onBack={onBack} />
      {entries.length === 0 && <EmptyState>No recent activity yet</EmptyState>}
      {entries.map((entry, i) => (
        <Card key={i} className="mb-2 flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-slate-900 dark:text-white">{entry.category} cleaned</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {entry.emailsCleaned} emails • {formatBytes(entry.bytesFreed)} freed
            </p>
          </div>
          <span className="text-xs text-slate-400">{timeAgo(entry.date)}</span>
        </Card>
      ))}
    </div>
  );
}
